import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	useHistory,
	useLocation
} from 'react-router';
import {
	Divider,
	Form,
	Icon,
	Image,
	Label,
	Message,
	Tab
} from 'semantic-ui-react';

import { stateOptions } from '../../../../../services/constants';
import {
	log,
	print
} from '../../../../../services/utils';

import { updateAgencyPhoto } from '../../../../../api/nora/agencies';
import { uploadAsset } from '../../../../../api/nora/assets';

import { useAuthState } from '../../../../../contexts/AuthContext';
import { useAgencyState } from '../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import DDFileUploadModal from '../../../../DDFileUpload';
import FormCheckbox from '../../../../Form/FormCheckbox';
import FormInput from '../../../../Form/FormInput';
import FormSelect from '../../../../Form/FormSelect';
import FormTextArea from '../../../../Form/FormTextArea';
import FormErrors from '../../../../Shared/FormErrors';

const ProfileTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { state: navigationState } = useLocation();

	const { user: { permissions } } = useAuthState();
	const { agencyTypes } = useAgencyState();
	const { setApiErrorAlert } = useUIAction();

	const [ fileUploadModal, setFileUploadModal ] = useState( false );
	const [ editable, setEditable ] = useState( !formData.id );
	const [ removeLogoConfirmAlert, setRemoveLogoConfirmAlert ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );

	useEffect( () => {
		setEditable( !formData.id );
	}, [ formData.id ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Agency'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			if ( navigationState?.to ) {
				history.push( navigationState.to );
			} else {
				history.push( '/agencies/directory' );
			}
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		navigationState,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editAgencies.canExecute,
		icon: 'pencil',
		permission: permissions.editAgencies.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		permissions
	] );

	const printAction = useMemo( () => ( {
		action: print,
		disable: validate(),
		icon: 'print',
		permission: true,
		text: 'Print'
	} ), [ validate ] );

	const duplicate = useCallback( () => {
		// eslint-disable-next-line no-unused-vars
		let { id, name, email, ...initialData } = formData;

		history.push( {
			pathname: '/agencies/directory/0',
			state: {
				text: `\u2190\u00A0\u00A0Back to ${ name }`,
				to: {
					pathname: `/agencies/directory/${ id }`,
					state: { activeIndex: 1 }
				},
				initialData
			}
		} );
	}, [ formData, history ] );

	const duplicateAction = useMemo( () => ( {
		action: duplicate,
		disable: validate(),
		helpId: 27,
		icon: 'add',
		permission: permissions.addAgencies.canExecute,
		text: 'Duplicate Agency'
	} ), [ duplicate, permissions.addAgencies.canExecute, validate ] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [
				duplicateAction,
				printAction,
				editAction
			],
		[
			cancelAction,
			duplicateAction,
			editable,
			saveAction,
			printAction,
			editAction
		]
	);

	const handleUploadLogo = useCallback( () => {
		setFileUploadModal( true );
	}, [] );

	const onUpload = useCallback( async ( { uid } ) => {
		if ( formData.id ) {
			try {
				const response = await updateAgencyPhoto( formData.id, uid );

				updateField( {
					target: {
						name: 'logo',
						value: response.uri
					}
				}, true );
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const handleRemoveLogo = useCallback( () => {
		setRemoveLogoConfirmAlert( true );
	}, [] );

	const removeLogo = useCallback( () => {
		if ( formData.id ) {
			try {
				updateAgencyPhoto( formData.id, 0 );

				updateField(
					{
						target: {
							name: 'logo',
							value: null
						}
					},
					true
				);
			} catch ( err ) {
				setApiErrorAlert( err );
			}
		}
	}, [
		formData.id,
		setApiErrorAlert,
		updateField
	] );

	const closeFileUploadModal = useCallback( () => {
		setFileUploadModal( false );
	}, [] );

	const closeRemoveLogoConfirmAlert = useCallback( () => {
		setRemoveLogoConfirmAlert( false );
	}, [] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email agency' ) }
			/>
			: null
		, [ errors.email ]
	);

	const websiteIcon = useMemo(
		() => formData.website
			? <Icon
				color="black"
				link
				name="globe"
				onClick={ () => console.log( 'visit agency website' ) }
			/>
			: null
		, [ formData.website ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	return (
		<Tab.Pane as="div">
			{ log( 'profileTab' ) }
			<Actions actions={ actions } />
			<div className="contact-form-container">
				<div className="no-print">
					<Image
						size="small"
						src={ formData.logo
							? formData.logo
							: 'https://react.semantic-ui.com/images/wireframe/image.png'
						}
					/>
					{ editable
						? formData.logo
							? <small className="image-upload-actions">
								<span onClick={ handleUploadLogo }>edit</span>&nbsp;&nbsp;|&nbsp;&nbsp;<span onClick={ handleRemoveLogo }>remove</span>
							</small>
							: <small className="image-upload-actions">
								<span onClick={ handleUploadLogo }>upload</span>
							</small>
						: null
					}
				</div>

				<Form
					autoComplete="off"
					error={ forceValidation && validate() }
				>
					<input
						className="hidden-input"
						name="name"
					/>

					<Form.Group>
						<FormInput
							errors={ errors.name }
							fieldLabel="Agency Name"
							forceValidation={ forceValidation }
							maxLength={ 255 }
							name="name"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.name || '' }
							width={ 10 }
						/>
						<FormSelect
							errors={ errors.type }
							fieldLabel="Agency Type"
							forceValidation={ forceValidation }
							name="type"
							onChange={ updateField }
							options={ agencyTypes }
							readOnly={ !editable }
							required
							value={ formData.type || '' }
							width={ 6 }
						/>
					</Form.Group>

					<Form.Group>
						<FormTextArea
							fieldLabel="Description"
							name="description"
							onChange={ updateField }
							readOnly={ !editable }
							rows={ 6 }
							value={ formData.description || '' }
							width={ 16 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							fieldLabel="Address"
							name="address"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							fieldLabel="Address Line 2"
							name="address2"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.address2 || '' }
							width={ 10 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							fieldLabel="City"
							name="city"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.city || '' }
							width={ 6 }
						/>
						<FormSelect
							fieldLabel="State"
							maxLength={ 2 }
							name="state"
							onChange={ updateField }
							options={ stateOptions }
							readOnly={ !editable }
							search
							value={ formData.state || '' }
							width={ 2 }
						/>
						<FormInput
							errors={ errors.zip }
							fieldLabel="Zip"
							forceValidation={ forceValidation }
							keyDown="integer"
							maxLength={ 5 }
							name="zip"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.zip || '' }
							width={ 2 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormInput
							errors={ errors.email }
							fieldLabel="Email"
							forceValidation={ forceValidation }
							icon={ emailIcon }
							maxLength={ 254 }
							name="email"
							readOnly={ !editable }
							required
							value={ formData.email || '' }
							onChange={ updateField }
							width={ 8 }
						/>
						<FormInput
							fieldLabel="Website"
							icon={ websiteIcon }
							maxLength={ 254 }
							name="website"
							readOnly={ !editable }
							value={ formData.website || '' }
							onChange={ updateField }
							width={ 8 }
						/>
					</Form.Group>

					<Form.Group>
						<FormInput
							errors={ errors.phone }
							fieldLabel="Phone"
							forceValidation={ forceValidation }
							keyDown="integer"
							inputLabel={ countryCode }
							mask="phone"
							maxLength={ 14 }
							name="phone"
							onChange={ updateField }
							readOnly={ !editable }
							required
							value={ formData.phone || '' }
							width={ 6 }
						/>
						<FormInput
							fieldLabel="Ext."
							forceValidation={ forceValidation }
							keyDown="integer"
							name="ext"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.ext || '' }
							width={ 2 }
						/>
						<FormCheckbox
							checked={ formData.SMSEnabled }
							fieldLabel="SMS Enabled"
							name="SMSEnabled"
							onChange={ updateField }
							readOnly={ !editable }
							width={ 3 }
						/>
					</Form.Group>
					<Form.Group>
						<FormInput
							errors={ errors.fax }
							fieldLabel="Fax"
							forceValidation={ forceValidation }
							keyDown="integer"
							inputLabel={ countryCode }
							mask="phone"
							maxLength={ 14 }
							name="fax"
							onChange={ updateField }
							readOnly={ !editable }
							value={ formData.fax || '' }
							width={ 8 }
						/>
					</Form.Group>

					<Divider
						hidden
						style={ { margin: '1.5rem 0' } }
					/>

					<Form.Group>
						<FormTextArea
							fieldLabel="Comments"
							name="comments"
							onChange={ updateField }
							readOnly={ !editable }
							rows={ 6 }
							value={ formData.comments || '' }
							width={ 16 }
						/>
					</Form.Group>
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
			<DDFileUploadModal
				closeModal={ closeFileUploadModal }
				onUpload={ onUpload }
				open={ fileUploadModal }
				upload={ uploadAsset }
			/>
			<Alert
				action={ removeLogo }
				actionText="Remove Logo"
				name="agency-remove-logo-confirm-alert"
				message="Are you sure?"
				open={ removeLogoConfirmAlert }
				closeModal={ closeRemoveLogoConfirmAlert }
				closeText="Cancel"
				type="confirm"
			/>
		</Tab.Pane>
	);
};

ProfileTab.propTypes = {
	errors: PropTypes.object.isRequired,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object.isRequired,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func.isRequired,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func.isRequired,
	validate: PropTypes.func.isRequired
};

export default memo( ProfileTab );
